import React, { useMemo } from 'react';
import { useTranslation } from '@app/core/i18n';

import { Options as PasswordStrengthOptions, passwordStrength } from 'check-password-strength';

import { PasswordStrength as MagmaPasswordStrength } from '@ast/magma/components/passwordstrength';

import { CharacterRequirement } from '@app/queryTyping';

import { Fields } from '@app/common/configurable-wizards';

export interface PasswordStrengthProps {
  policySettings: Fields['Password']['passwordPolicy'];
  passwordValue: string;
}

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({ policySettings, passwordValue }) => {
  const { t } = useTranslation();
  const PasswordStrengthLabels = {
    None: t('strength-labels.none|None strength label', 'Strength:'),
    VeryWeak: t('strength-labels.veryweak|Very weak strength label', 'Strength: Very weak'),
    Week: t('strength-labels.weak|Weak strength label', 'Strength: Weak'),
    Fair: t('strength-labels.fair|Fair strength label', 'Strength: Fair'),
    Strong: t('strength-labels.strong|Strong strength label', 'Strength: Strong'),
    VeryStrong: t('strength-labels.verystrong|Very strong strength label', 'Strength: Very strong'),
  };

  const maxDiversity = useMemo(() => {
    let diversity = 0;
    if (!policySettings) { return diversity; }

    // lover case diversity
    if (policySettings.lowerCaseRequirement !== CharacterRequirement.NOTALLOWED) {
      diversity += 1;
    }

    // upper case diversity
    if (policySettings.upperCaseRequirement !== CharacterRequirement.NOTALLOWED) {
      diversity += 1;
    }

    // numbers diversity
    if (policySettings.numberRequirement !== CharacterRequirement.NOTALLOWED) {
      diversity += 1;
    }

    // special character diversity
    if (policySettings.specialCharacterRequirement !== CharacterRequirement.NOTALLOWED) {
      diversity += 1;
    }

    return diversity;
  }, [policySettings]);

  const defaultPasswordStrengthOptions = [
    {
      id: 0,
      value: PasswordStrengthLabels.None,
      minDiversity: 0,
      minLength: 0,
    },
    {
      id: 1,
      value: PasswordStrengthLabels.VeryWeak,
      minDiversity: 0,
      minLength: 1,
    },
    {
      id: 2,
      value: PasswordStrengthLabels.Week,
      minDiversity: maxDiversity - 2,
      minLength: 4,
    },
    {
      id: 3,
      value: PasswordStrengthLabels.Fair,
      minDiversity: maxDiversity - 1,
      minLength: 6,
    },
    {
      id: 4,
      value: PasswordStrengthLabels.Strong,
      minDiversity: maxDiversity,
      minLength: 8,
    },
    {
      id: 5,
      value: PasswordStrengthLabels.VeryStrong,
      minDiversity: maxDiversity,
      minLength: 12,
    },
  ];

  return (
    <MagmaPasswordStrength
      strength={
        passwordStrength(
          passwordValue,
          defaultPasswordStrengthOptions as PasswordStrengthOptions<string>,
        ).id as any
      }
      labels={
        Object.values(PasswordStrengthLabels) as [string, string, string, string, string, string]
      }
    />
  );
};
